/**
 * Assortment Utilities
 * Utility functions for extracting and formatting assortment-related options
 * from RPN attributes and versions data
 */
import { MultiselectProps } from '@amzn/awsui-components-core';
import { ItemMetadata } from 'src/types/common/api.types';
import { RpnVersion, StandardAttribute } from 'src/types/rpn/rpnDetails.type';

/**
 * Option item structure for multiselect dropdowns
 */
export interface AssortmentOptionItem {
  label: string;
  value: string;
}

/**
 * Finds a standard attribute by its type label
 */
const findAttributeByTypeLabel = (
  standardAttributes: StandardAttribute[] | null | undefined,
  typeLabel: string
): StandardAttribute | undefined => {
  if (!standardAttributes) return undefined;
  return standardAttributes.find((attr) => attr.attribute_type_label === typeLabel);
};

/**
 * Extracts selected values from a standard attribute and converts to dropdown options
 * Since selected_values is now string[] | null, each value serves as both label and value
 */
const extractOptionsFromAttribute = (attribute: StandardAttribute | undefined): AssortmentOptionItem[] => {
  if (!attribute || !attribute.selected_values) return [];

  return attribute.selected_values
    .filter((sv): sv is string => sv !== null && sv !== undefined && sv.trim() !== '')
    .map((sv) => ({
      label: sv,
      value: sv,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Extracts channel options from standard_attributes
 * Looks for attribute_type_label: "CHANNEL"
 */
export const getChannelOptions = (
  standardAttributes: StandardAttribute[] | null | undefined
): AssortmentOptionItem[] => {
  const channelAttribute = findAttributeByTypeLabel(standardAttributes, 'CHANNEL');
  return extractOptionsFromAttribute(channelAttribute);
};

/**
 * Extracts country options from standard_attributes
 * Looks for attribute_type_label: "COUNTRY"
 */
export const getCountryOptions = (
  standardAttributes: StandardAttribute[] | null | undefined
): AssortmentOptionItem[] => {
  const countryAttribute = findAttributeByTypeLabel(standardAttributes, 'COUNTRY');
  return extractOptionsFromAttribute(countryAttribute);
};

/**
 * Extracts SKU type options from standard_attributes
 * Looks for attribute_type_label: "SKU_TYPE"
 */
export const getSkuTypeOptions = (
  standardAttributes: StandardAttribute[] | null | undefined
): AssortmentOptionItem[] => {
  const skuTypeAttribute = findAttributeByTypeLabel(standardAttributes, 'SKU_TYPE');
  return extractOptionsFromAttribute(skuTypeAttribute);
};

/**
 * Extracts product version options from versions array
 * Uses version_description as label, version_id as value
 */
export const getProductVersionOptions = (versions: RpnVersion[] | null | undefined): AssortmentOptionItem[] => {
  if (!versions) return [];

  return versions
    .filter((v) => v.version_id !== null && v.version_id !== undefined && v.version_description)
    .map((v) => ({
      label: v.version_description,
      value: String(v.version_id),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Converts AssortmentOptionItem array to MultiselectProps.Option array
 */
export const toMultiselectOptions = (options: AssortmentOptionItem[]): MultiselectProps.Option[] => {
  return options.map((opt) => ({
    label: opt.label,
    value: opt.value,
  }));
};

/**
 * Generates all combinations (cartesian product) of multiple arrays
 * Used to create all possible assortment combinations from selected options
 *
 * @example
 * generateCombinations([['A', 'B'], ['1', '2']]) => [['A', '1'], ['A', '2'], ['B', '1'], ['B', '2']]
 */
export const generateCombinations = <T>(arrays: T[][]): T[][] => {
  const combinations: T[][] = [];

  const helper = (index: number, currentCombination: T[]): void => {
    if (index === arrays.length) {
      combinations.push([...currentCombination]);
      return;
    }

    for (let i = 0; i < arrays[index].length; i++) {
      currentCombination.push(arrays[index][i]);
      helper(index + 1, currentCombination);
      currentCombination.pop();
    }
  };

  helper(0, []);
  return combinations;
};

/**
 * Selection item with attribute name for mapping to row fields
 */
export interface SelectionItem {
  label: string;
  value: string;
  attrName: string;
}

/**
 * Row data structure for assortment combinations
 */
export interface AssortmentRow {
  channel: string;
  country: string;
  sku_type: string;
  version_description: string;
  version_id: number;
  comment: string;
}

/**
 * AssortmentRow with ItemMetadata for API submission
 * Used when creating new assortments via the API
 */
export interface AssortmentRowWithMetadata {
  channel: string;
  country: string;
  sku_type: string;
  version_description: string;
  version_id: number;
  item_metadata: ItemMetadata;
}

/**
 * Converts AssortmentRow array to AssortmentRowWithMetadata array for API submission
 * Adds ItemMetadata with created_by and updated_by set to the current user
 *
 * @param rows - Array of AssortmentRow objects (only selectable rows without comments)
 * @param username - The username of the current user for created_by/updated_by
 * @returns Array of AssortmentRowWithMetadata ready for API submission
 */
export const toAssortmentRowsWithMetadata = (rows: AssortmentRow[], username: string): AssortmentRowWithMetadata[] => {
  const now = new Date().toISOString();

  return rows
    .filter((row) => !row.comment) // Only include selectable rows (no validation errors)
    .map((row) => ({
      channel: row.channel,
      country: row.country,
      sku_type: row.sku_type,
      version_description: row.version_description,
      version_id: row.version_id,
      item_metadata: {
        is_active: true,
        created_by: username,
        created_at: now,
        updated_by: username,
        updated_at: now,
      },
    }));
};

/**
 * Applies smart validation rules to an assortment row
 * Sets comment field if the combination is not valid
 *
 * Business Rules:
 * 1. No Online Channel for SKU types - Demo
 * 2. No Offline channel for Certified Refurb, Refurb, Warranty
 * 3. No Special offers for Demo SKU types with DTCP product versions (Non-DTCP allowed)
 */
export const applySmartAddValidation = (row: AssortmentRow, existingAssortments?: Set<string>): AssortmentRow => {
  // No Online Channel for SKU types - Demo
  if (row.channel === 'ONLINE' && row.sku_type === 'DEMO') {
    return { ...row, comment: 'No Online Channel for SKU types - Demo' };
  }

  // No Offline channel for Certified Refurb, Refurb, Warranty
  if (row.channel === 'OFFLINE' && ['CERTIFIED REFURB', 'REFURB', 'WARRANTY'].includes(row.sku_type)) {
    return { ...row, comment: 'No Offline channel for Certified Refurb, Refurb, Warranty.' };
  }

  // No Special offers for Demo SKU types with DTCP product versions
  // Split version_description into parts and check for exact "DTCP" value (Non-DTCP is allowed)
  const versionDescriptionParts = row.version_description.split(/[,\s]+/).map((part) => part.trim());
  if (row.sku_type === 'DEMO' && versionDescriptionParts.includes('DTCP')) {
    return { ...row, comment: 'No Special offers for Demo and Refurb SKU types.' };
  }

  // Check if assortment already exists
  if (existingAssortments) {
    const key = `${row.channel}_${row.country}_${row.sku_type}_${row.version_description}`;
    if (existingAssortments.has(key)) {
      return { ...row, comment: 'Assortment already exists.' };
    }
  }

  return row;
};

/**
 * Generates assortment combination row data from selected options
 * Creates cartesian product of all selections
 * Uses label (display text) for row data, value (ID) for unique identification
 * Applies smart validation rules to determine if rows are selectable
 */
export const generateAssortmentRows = (
  channels: SelectionItem[],
  countries: SelectionItem[],
  skuTypes: SelectionItem[],
  productVersions: SelectionItem[],
  existingAssortments?: Set<string>
): AssortmentRow[] => {
  // If any array is empty, return empty array
  if (!channels.length || !countries.length || !skuTypes.length || !productVersions.length) {
    return [];
  }

  const combinations = generateCombinations([channels, countries, skuTypes, productVersions]);

  return combinations.map((combination) => {
    const row: AssortmentRow = {
      channel: '',
      country: '',
      sku_type: '',
      version_description: '',
      version_id: 0,
      comment: '',
    };

    combination.forEach((item) => {
      // Use label (display text) for row data, value (ID) for version_id
      if (item.attrName === 'channel') {
        row.channel = item.label;
      } else if (item.attrName === 'country') {
        row.country = item.label;
      } else if (item.attrName === 'sku_type') {
        row.sku_type = item.label;
      } else if (item.attrName === 'product_version') {
        row.version_description = item.label;
        row.version_id = parseInt(item.value, 10) || 0;
      }
    });

    // Apply smart validation rules
    return applySmartAddValidation(row, existingAssortments);
  });
};
